import React, { useEffect, useState } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { useSessionLogoutSubscription } from '../../account/localLogout';
import AiCopilotProvider from '../../ai/AiCopilotProvider';
import ErrorBoundary from '../../components/ErrorBoundary';
import { AllGroupsProvider } from '../../groups/useAllGroups';
import { AllSlimPostsProvider } from '../../post/useAllSlimPosts';
import { LayoutSizeProvider } from '../../post/utils/useLayoutSize';
import { AllTopicsProvider } from '../../topic/useAllTopics';
import { AllUsersProvider } from '../../user/useAllUsers';
import BeforeLeaveConfirmContextProvider from '../../utils/BeforeLeaveConfirm';
import { MainContentHistoryProvider } from '../../utils/router/useMainContentHistory';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import { IsPublicViewProvider } from '../../utils/useIsPublicView';
import usePreferDesktopNotice from '../../utils/usePreferDesktopNotice';
import useShortcut from '../../utils/useShortcut';
import useVisibilityChangeDetection from '../../utils/useVisibilityChangeDetection';
import { useVersionCheck } from '../checkVersion';
import EnsureAdmin from '../router/EnsureAdmin';
import EnsureAnonymous from '../router/EnsureAnonymous';
import EnsureMinSize from '../router/EnsureMinSize';
import EnsureUser from '../router/EnsureUser';
import EnsureUserOrAnonymous from '../router/EnsureUserOrAnonymous';
import Redirect from '../router/Redirect';
import useRouteHandle from '../useRouteHandle';
import GoogleAnalytics from './GoogleAnalytics';
import RightPanel from './RightPanel';
import SidebarShiftStateProvider from './SidebarShiftStateProvider';

const RENDER_OP = 'ui.react.render';

const now = () => Date.now() / 1000;

const useRouteChangeProfiler = (routeName: string) => {
  const [currentRender, setCurrentRender] = useState<{
    startTime: number;
    routeName: string;
  }>(() => ({
    startTime: now(),
    routeName,
  }));

  if (window.SentryTracing && currentRender.routeName !== routeName) {
    setCurrentRender({
      startTime: now(),
      routeName,
    });
  }

  useEffect(() => {
    if (!window.SentryTracing) return;

    // We start and end the span immediately in useEffect so
    // that it always gets attached to the "navigation" root span
    window.SentryTracing.startInactiveSpan({
      name: `Render /${currentRender.routeName}`,
      onlyIfParent: true,
      startTime: currentRender.startTime,
      op: RENDER_OP,
      attributes: {
        [window.SentryTracing.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]:
          'auto.ui.react.profiler',
        'ui.route_name': currentRender.routeName,
      },
    }).end(now());
  }, [currentRender]);
};

const ROLE_WRAPPER = {
  user: EnsureUser,
  admin: EnsureAdmin,
  anonymous: EnsureAnonymous,
  userOrAnonymous: EnsureUserOrAnonymous,
};

const AppRoot = () => {
  const [topicId] = useSlug('topicSlug');

  const navigate = useNavigate();

  useShortcut('shift+n', (e) => {
    e.preventDefault();
    navigate(createPostPath({ topicId: topicId || undefined }));
  });

  useVersionCheck();
  usePreferDesktopNotice();
  useSessionLogoutSubscription();
  useVisibilityChangeDetection();

  const location = useLocation();
  const {
    routeName,
    pageLayout = {},
    ensureRole,
    ensureMinSize,
  } = useRouteHandle();

  useRouteChangeProfiler(routeName);

  useEffect(() => {
    if (routeName) {
      analytics.page(routeName);
      window.Intercom?.('update');
    }
  }, [routeName, location]);

  const EnsureRoleWrapper =
    (ensureRole && ROLE_WRAPPER[ensureRole]) || React.Fragment;

  const ensureMinSizeOptions =
    typeof ensureMinSize === 'object'
      ? ensureMinSize
      : { minSize: ensureMinSize, fallback: <Redirect to="/" /> };

  return (
    <AllUsersProvider>
      <AllGroupsProvider>
        <AllTopicsProvider>
          <AllSlimPostsProvider>
            <LayoutSizeProvider>
              <MainContentHistoryProvider skipLocation={!pageLayout.sidebar}>
                <ErrorBoundary name="scrollRestoration" nonFatal>
                  <ScrollRestoration />
                </ErrorBoundary>
                <RightPanel.Provider>
                  <EnsureMinSize
                    minSize={ensureMinSizeOptions.minSize}
                    fallback={ensureMinSizeOptions.fallback}
                  >
                    <BeforeLeaveConfirmContextProvider>
                      <EnsureRoleWrapper>
                        <SidebarShiftStateProvider>
                          <AiCopilotProvider>
                            <Outlet />
                          </AiCopilotProvider>
                        </SidebarShiftStateProvider>
                      </EnsureRoleWrapper>
                    </BeforeLeaveConfirmContextProvider>
                  </EnsureMinSize>
                </RightPanel.Provider>
                <GoogleAnalytics />
              </MainContentHistoryProvider>
            </LayoutSizeProvider>
          </AllSlimPostsProvider>
        </AllTopicsProvider>
      </AllGroupsProvider>
    </AllUsersProvider>
  );
};

export default () => (
  <IsPublicViewProvider>
    <AppRoot />
  </IsPublicViewProvider>
);
